import type { Rgb } from '@/lib/ar';
import type { JSX } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { COMPARE_LIMIT, drop, pick } from './compare';
import {
  CompareGrid,
  DesignStrip,
  Disclaimer,
  LightCheck,
  ModeTabs,
  PhotoModeNotice,
  ShadeChip,
} from './components';
import { PhotoTab } from './PhotoTab';
import { useTryOnDeps } from './context';
import type { CalibrationProblem, WhiteBalance } from './kiosk';
import { calibrate } from './kiosk';
import { EMPTY, type PipelineState, belowUsableFps, fpsForApi, onFrame, tried } from './pipeline';
import { useDesigns, useOpenSession, usePatchSession, useShades } from './queries';
import { TRYON_NAMESPACE } from './strings';

/**
 * BP-21 §6: `/try-on`, replacing BP-11 R10's five-second placeholder.
 *
 * **What this component is responsible for, and what it is not.** It owns
 * the sitting — opening it, recording what she tried, ending it — and the
 * decisions rule 5 and rule 2 make. It does not own a frame: the camera
 * stream is bound to a `<video>` and the landmark/segment/recolour loop is
 * `src/lib/ar` under a worker, which is where §9 says it belongs.
 *
 * **The session is opened when the camera starts, not when the screen
 * mounts.** A client who opened the page and left would otherwise be a
 * session with no shades and no booking in T7's report, which is exactly
 * the row that makes the conversion rate read as a failure.
 *
 * **It is ended on unmount, with the FPS average.** The api has no sweeper
 * for stale sessions (§8: "a sweeper that closed stale ones would be
 * inventing an `ended_at` nobody observed"), so the only honest `ended_at`
 * is the one the device observes. A client who kills the app leaves a
 * session open, and that is the truth about what happened.
 */
export function TryOnScreen(): JSX.Element {
  const { t } = useTranslation(TRYON_NAMESPACE);
  const { mode, openCamera, closeCamera, now, onBookThisLook, sampleGreyCard, onCalibrated } =
    useTryOnDeps();
  const shades = useShades();
  const designs = useDesigns();
  const open = useOpenSession();
  const patch = usePatchSession();

  const [tab, setTab] = useState<'live' | 'photo'>('live');
  const [designId, setDesignId] = useState<string | null>(null);
  const [camera, setCamera] = useState<'idle' | 'live' | 'unavailable'>('idle');
  // The sitting's id twice: as state for the render, and as a ref for the
  // unmount cleanup, which runs once with the closure it was created with
  // and cannot see a later state value. Reading the ref during render is
  // what React's own lint refuses, and rightly — under concurrent
  // rendering a render can be thrown away.
  const [openSessionId, setOpenSessionId] = useState<string | null>(null);
  const [chosen, setChosen] = useState<readonly string[]>([]);
  const [pipeline, setPipeline] = useState<PipelineState>(EMPTY);
  const [light, setLight] = useState<{
    sample: Rgb | null;
    balance: WhiteBalance | null;
    problem: CalibrationProblem | null;
  }>({ sample: null, balance: null, problem: null });

  const stream = useRef<MediaStream | null>(null);
  const video = useRef<HTMLVideoElement | null>(null);
  const sessionId = useRef<string | null>(null);

  /**
   * What the unmount cleanup needs, kept current by an effect.
   *
   * The cleanup runs once, with the closure it was created with, and what
   * it has to send is the *final* FPS average and the *current* mutation —
   * neither of which exists when the component first mounts. Writing the
   * refs during render instead would be reading and writing a ref in the
   * render phase, which React's own lint refuses and which breaks under
   * concurrent rendering, where a render can be thrown away.
   */
  const latest = useRef<{
    pipeline: PipelineState;
    send: typeof patch.mutate;
    shut: typeof closeCamera;
  }>({ pipeline: EMPTY, send: patch.mutate, shut: closeCamera });
  useEffect(() => {
    latest.current = { pipeline, send: patch.mutate, shut: closeCamera };
  });

  const start = useCallback(async () => {
    const opened = await openCamera();
    if (opened === null) {
      setCamera('unavailable');
      return;
    }
    stream.current = opened;
    setCamera('live');
    // Only now. See the note above: a session opened on mount is a row in
    // T7's report for somebody who never pointed a camera at anything.
    const session = await open.mutateAsync();
    sessionId.current = session.id;
    setOpenSessionId(session.id);
  }, [open, openCamera]);

  /**
   * Bind the stream the moment React gives us the element.
   *
   * A callback ref and not an assignment in `start`, for BP-11 R10's
   * reason: the `<video>` is rendered only once the state is `live`, so at
   * the moment the stream arrives there is no element to bind it to.
   */
  const attach = useCallback((element: HTMLVideoElement | null) => {
    video.current = element;
    if (element !== null) {
      element.srcObject = stream.current;
    }
  }, []);

  /**
   * One frame arrived.
   *
   * The clock is read **here**, not inside the updater. React batches, and a
   * functional update runs when the batch flushes — so reading `now()` in
   * the updater timestamps every frame in a batch with the moment the batch
   * was flushed, every interval comes out as zero, and the FPS figure is
   * `null` on a device that was managing sixty. The timestamp belongs to the
   * frame, not to the render that noticed it.
   */
  const frame = useCallback(() => {
    const at = now();
    setPipeline((current) => onFrame(current, at));
  }, [now]);

  const choose = useCallback(
    (skuId: string) => {
      setChosen((current) => pick(current, skuId));
      setPipeline((current) => tried(current, skuId));
      const id = sessionId.current;
      if (id !== null) {
        // Fire and forget: a failed analytics write must not take the shade
        // off her nails. The next tap sends the whole list again, so one
        // dropped request loses nothing.
        patch.mutate({ sessionId: id, patch: { shades_tried: [skuId] } });
      }
    },
    [patch],
  );

  // Shut the camera and close the sitting, whatever else happened.
  useEffect(
    () => () => {
      const { pipeline: final, send, shut } = latest.current;
      shut(stream.current);
      stream.current = null;
      const id = sessionId.current;
      if (id !== null) {
        send({ sessionId: id, patch: { ended: true, ...fpsPatch(final) } });
      }
    },
    [],
  );

  /**
   * T7's light check.
   *
   * The verdict is kept on the screen *and* handed to the render loop: the
   * screen needs it to show the two swatches, and the loop needs it to
   * correct the frames. A failed check clears nothing — the last good
   * calibration stays in force, because a lamp somebody walked in front of
   * is not a reason to go back to an uncorrected camera.
   */
  const checkLight = useCallback(() => {
    const element = video.current;
    const sample = element === null ? null : (sampleGreyCard?.(element) ?? null);
    if (sample === null) {
      // No camera yet, or a frame nothing could be read from. Reported as a
      // dark sample rather than invented as a neutral one: a calibration
      // that silently succeeded against nothing is worse than none.
      setLight((current) => ({ ...current, problem: 'too_dark' }));
      return;
    }
    const verdict = calibrate(sample);
    if (verdict.problem !== undefined) {
      setLight((current) => ({ ...current, problem: verdict.problem }));
      return;
    }
    setLight({ sample, balance: verdict.balance, problem: null });
    onCalibrated?.(verdict.balance);
  }, [onCalibrated, sampleGreyCard]);

  const rows = shades.data ?? [];
  const chosenShades = chosen
    .map((skuId) => rows.find((shade) => shade.sku_id === skuId))
    .filter((shade): shade is (typeof rows)[number] => shade !== undefined);
  const last = chosenShades[chosenShades.length - 1];
  const chosenDesign = (designs.data ?? []).find((design) => design.id === designId);

  return (
    <section data-testid="try-on" className="flex flex-col gap-3">
      <h1 className="text-lg font-semibold">{t('title')}</h1>
      <p className="text-sm text-muted-foreground" data-testid="try-on-lead">
        {mode === 'tablet' ? t('kioskLead') : t('lead')}
      </p>

      <ModeTabs tab={tab} onPick={setTab} />

      {tab === 'live' && camera === 'live' ? (
        <video
          data-testid="try-on-preview"
          ref={attach}
          autoPlay
          playsInline
          muted
          onTimeUpdate={frame}
          className="w-full rounded bg-foreground"
        />
      ) : tab === 'live' ? (
        <>
          <p className="text-sm" data-testid={`camera-${camera}`}>
            {camera === 'idle' ? t('camera.prompt') : t('camera.unavailable')}
          </p>
          <button
            type="button"
            data-testid="try-on-start"
            className="self-start rounded bg-primary px-3 py-1 text-sm text-primary-foreground"
            onClick={() => {
              void start();
            }}
          >
            {t('camera.start')}
          </button>
        </>
      ) : (
        <PhotoTab
          {...(chosen[chosen.length - 1] === undefined
            ? {}
            : { shadeSkuId: chosen[chosen.length - 1] as string })}
          {...(chosenDesign === undefined ? {} : { design: chosenDesign })}
          {...(openSessionId === null ? {} : { sessionId: openSessionId })}
          onClearDesign={() => {
            setDesignId(null);
          }}
        />
      )}

      {tab === 'live' && belowUsableFps(pipeline) && (
        <PhotoModeNotice
          onUsePhoto={() => {
            setTab('photo');
          }}
        />
      )}

      {mode === 'tablet' && (
        <LightCheck
          sample={light.sample}
          balance={light.balance}
          problem={light.problem}
          onCheck={checkLight}
        />
      )}

      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold">{t('shades.heading')}</h2>
        {rows.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="shades-empty">
            {t('shades.none')}
          </p>
        ) : (
          <div className="flex gap-2 overflow-x-auto" data-testid="shade-strip">
            {rows.map((shade) => (
              <ShadeChip
                key={shade.sku_id}
                shade={shade}
                chosen={chosen.includes(shade.sku_id)}
                onPick={choose}
              />
            ))}
          </div>
        )}
      </section>

      <DesignStrip designs={designs.data ?? []} chosen={designId} onPick={setDesignId} />

      <CompareGrid
        shades={chosenShades}
        onRemove={(skuId) => {
          setChosen((current) => drop(current, skuId));
        }}
      />

      {last !== undefined && (
        <div className="flex flex-col gap-1">
          <button
            type="button"
            data-testid="book-this-look"
            className="rounded bg-primary px-3 py-2 text-sm text-primary-foreground"
            onClick={() => {
              // `last` is what made this button render, so there is no
              // "nothing picked" arm here to guard — and a guard for it
              // would be a branch no click could take.
              onBookThisLook(last.sku_id);
            }}
          >
            {t('book.action')}
          </button>
          <p className="text-xs text-muted-foreground">{t('book.hint', { shade: last.shade_name })}</p>
        </div>
      )}

      {tab === 'live' && <Disclaimer />}
    </section>
  );
}

/**
 * `{fps_avg}` when there is one, `{}` when there is not.
 *
 * Spread rather than `fps_avg: undefined`, because
 * `exactOptionalPropertyTypes` is on and because an explicit `undefined`
 * would serialise as a key the api would read as "measured, and it was
 * nothing".
 */
function fpsPatch(state: PipelineState): { fps_avg?: string } {
  const measured = fpsForApi(state);
  return measured === undefined ? {} : { fps_avg: measured };
}

/** Re-exported so a test can assert the grid's capacity from the screen's module. */
export { COMPARE_LIMIT };
